/**
 * Phase 3.27 — Stammdaten (DealerProfileSection).
 * Größtes Sub-Modul: Firmenname, Logo, Adresse, Kontaktdaten, Händlertypen,
 * Werkstatt-Konfiguration, Teile-Shop. Eingeklappt by default; speichert
 * über PATCH /api/users/me/dealer-profile.
 */
import { useEffect, useState, useCallback } from "react";
import { Building2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { CheckRow, useEmployees, EmployeeSelect } from "./_shared";
import api from "../../lib/api";
import { useVehicleCategories } from "../../lib/systemConfig";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { UnternehmensdatenBlock } from "./UnternehmensdatenBlock";
import BranchOverrideToggle from "./BranchOverrideToggle";
import { useAutosave } from "../../hooks/useAutosave";
import { useLockedFields } from "../../hooks/useLockedFields";
import SaveStatusIndicator from "./SaveStatusIndicator";
import useSettingsExpandAll from "../../lib/useSettingsExpandAll";

export function DealerProfileSection({ currentUser, onDirtyChange }) {
    const { refresh } = useAuth();
    const { isLocked, lockProps, anyLocked } = useLockedFields();
    // Phase 3.20 — Stammdaten startet eingeklappt, User klickt auf den Header zum Aufklappen.
    const [expanded, setExpanded] = useState(false);
    useSettingsExpandAll(setExpanded);
    const initialForm = {
        company_name: currentUser.company_name || "",
        company_logo_url: currentUser.company_logo_url || "",
        homepage_url: currentUser.homepage_url || "",
        street: currentUser.street || "",
        postal_code: currentUser.postal_code || "",
        city: currentUser.city || "",
        country: currentUser.country || "Deutschland",
        use_address_as_listing_location: !!currentUser.use_address_as_listing_location,
        site_code: currentUser.site_code || "",
        tax_id: currentUser.tax_id || "",
        vat_id: currentUser.vat_id || "",
        phone_office: currentUser.phone_office || "",
        phone_mobile: currentUser.phone_mobile || "",
        collective_phone: currentUser.collective_phone || "",
        collective_email: currentUser.collective_email || "",
        contact_email: currentUser.contact_email || currentUser.email || "",
        contact_first_name: currentUser.contact_first_name || "",
        contact_last_name: currentUser.contact_last_name || "",
        // Jun-2026 (User-Wunsch, Etappe 2) — Ansprechpartner = Mitarbeiter ODER = GF/Inhaber
        ansprechpartner_employee_id: currentUser.ansprechpartner_employee_id || "",
        ansprechpartner_same_as_gf: !!currentUser.ansprechpartner_same_as_gf,
        // Phase 3.44 Iter 2 — Verkaufskategorien (von WerkstattSection nach Stammdaten verschoben)
        dealer_motorcycles: currentUser.dealer_motorcycles !== false,
        dealer_bicycles: !!currentUser.dealer_bicycles,
        dealer_cars: !!currentUser.dealer_cars,
        sales_hours_enabled: !!currentUser.sales_hours_enabled,
        sales_hours: currentUser.sales_hours || "",
    };
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    // Impressum-Modal-State (Phase 2)
    const [imprintOpen, setImprintOpen] = useState(false);
    const [imprintText, setImprintText] = useState("");
    const [imprintLoading, setImprintLoading] = useState(false);
    // Jun-2026 (User-Wunsch) — Hauptsitz kann das Impressum auf alle Filialen übertragen.
    const isHauptsitz = currentUser.branch_role === "hauptsitz";
    const [applyToBranches, setApplyToBranches] = useState(false);
    const employees = useEmployees();
    const vehCats = useVehicleCategories();
    // Jun-2026 (User-Wunsch) — nur eigene Mitarbeiter dieses Standorts (org-weit ausschließen).
    const ownEmployees = (employees || []).filter((e) => e.employer_dealer_id === currentUser?.id);

    // Jun-2026 (User-Wunsch): Stammdaten speichern jetzt automatisch (Autospeichern)
    // mit grüner „Gespeichert"-Anzeige im Kopf. Daher meldet diese Sektion keine
    // „ungespeicherten Änderungen" mehr an die globale Banner-Leiste.
    useEffect(() => {
        onDirtyChange?.(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    // Autospeichern (debounced) — PATCHt das Profil ohne den Impressum-Schritt.
    // Bei ungültigem Standort-Code wird der Save übersprungen (dirty bleibt).
    const autosaveProfile = useCallback(async (data) => {
        const sc = (data.site_code || "").trim();
        if (sc && sc.length < 2) {
            toast.error("Standort-Code muss 2–4 Großbuchstaben haben (oder leer bleiben).");
            throw new Error("invalid site_code");
        }
        await api.patch("/users/me/dealer-profile", { ...data });
        await refresh();
    }, [refresh]);
    const sig = useAutosave(form, autosaveProfile, { delay: 1000, enabled: expanded });

    const uploadLogo = async (file) => {
        if (!file) return;
        setLogoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/upload/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            update("company_logo_url", r.data.url);
            toast.success("Profilbild hochgeladen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
        } finally {
            setLogoUploading(false);
        }
    };

    // „Impressum prüfen & bestätigen" — Stammdaten sind bereits autogespeichert;
    // hier wird nur der generierte Impressum-Text geladen und der Bestätigungs-
    // Dialog geöffnet (Bestätigungsschritt bleibt bewusst manuell erhalten).
    const openImprint = async () => {
        setImprintLoading(true);
        try {
            sig.saveNow?.();
            const r = await api.get("/users/me/imprint/preview");
            setImprintText(r.data.current_imprint_text || r.data.imprint_text);
            setImprintOpen(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vorschau konnte nicht geladen werden");
        } finally {
            setImprintLoading(false);
        }
    };

    const confirmImprint = async () => {
        const t = (imprintText || "").trim();
        if (t.length < 10) {
            toast.error("Bitte einen aussagekräftigen Impressums-Text eingeben");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/users/me/dealer-profile", { imprint_text: t });
            if (isHauptsitz && applyToBranches) {
                try {
                    const r = await api.post("/users/me/imprint/apply-to-branches");
                    toast.success(`Impressum auf ${r.data?.updated ?? 0} Filiale(n) übertragen.`);
                } catch { toast.error("Übertragung auf Filialen fehlgeschlagen."); }
            }
            await refresh();
            setImprintOpen(false);
            toast.success("Impressum gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    // Profil-Daten (inkl. Öffnungszeiten) wurden bereits in Phase 1 gespeichert.
    // Beim Schließen des Impressum-Dialogs ohne Bestätigung synchronisieren wir
    // die Anzeige mit dem Server-Stand, damit keine bereits gespeicherte Änderung
    // (z. B. „Öffnungszeiten Verkauf") fälschlich als „nicht gespeichert" wirkt.
    const dismissImprint = async () => {
        if (saving) return;
        setImprintOpen(false);
        await refresh();
        toast.success("Profil gespeichert. Impressum wurde nicht geändert.");
    };

    const regenerateImprint = async () => {
        setImprintLoading(true);
        try {
            const r = await api.get("/users/me/imprint/preview");
            setImprintText(r.data.imprint_text); // always the fresh auto-generated text
            toast.success("Impressum neu generiert");
        } catch (e) {
            toast.error("Vorschau konnte nicht geladen werden");
        } finally {
            setImprintLoading(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="dealer-profile-section">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className={`w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors ${expanded ? "border-b border-gray-200" : ""}`}
                data-testid="dealer-profile-toggle"
                aria-expanded={expanded}
            >
                <Building2 className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold">Stammdaten</h2>
                    <p className="text-xs text-gray-600 mt-1">Diese Daten erscheinen auf Ihren Inseraten und in der öffentlichen Händlerseite.</p>
                </div>
                <span className="mr-2 shrink-0" data-testid="dealer-profile-save-status" onClick={(e) => e.stopPropagation()}>
                    <SaveStatusIndicator saving={sig.saving} dirty={sig.dirty} savedAt={sig.savedAt} />
                </span>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            {expanded && (
            <div className="px-6 pb-6" data-testid="dealer-profile-body">

            {/* Stammdaten: Firmenname / Logo / Kunden-Kennung */}
            <div className="border border-gray-200 rounded-sm p-4 mb-5 bg-[#FFFBEB]/40" data-testid="dealer-identity-block">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#92400E] mb-3">Stammdaten</div>
                <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-4 items-start">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Firmen-Logo</Label>
                        <div className="mt-2 w-[120px] h-[120px] border-2 border-[#111827] rounded-sm bg-white flex items-center justify-center overflow-hidden" data-testid="dealer-logo-preview">
                            {form.company_logo_url ? (
                                <img src={form.company_logo_url} alt="Logo" className="w-full h-full object-contain" />
                            ) : (
                                <Building2 className="w-8 h-8 text-gray-300" />
                            )}
                        </div>
                        {isLocked("company_logo_url") ? (
                            <div className="mt-2 text-[11px] text-gray-500 text-center border border-gray-200 bg-gray-50 rounded-sm px-2 py-1.5" data-testid="dealer-logo-locked-hint">
                                Vom Hauptsitz zentral vorgegeben
                            </div>
                        ) : (
                        <label className="mt-2 inline-flex items-center justify-center w-full h-9 rounded-sm bg-[#111827] hover:bg-black text-white text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors" data-testid="dealer-logo-upload-label">
                            {logoUploading ? "…" : (form.company_logo_url ? "Ändern" : "Hochladen")}
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => uploadLogo(e.target.files?.[0])}
                                data-testid="dealer-logo-upload-input"
                            />
                        </label>
                        )}
                        <div className="mt-2 flex items-center justify-between gap-2" data-testid="logo-branch-override-row">
                            <span className="text-[11px] text-gray-500">Firmen-Logo organisationsweit</span>
                            <BranchOverrideToggle area="logo" currentUser={currentUser}
                                infoText="Firmen-Logo des Hauptsitzes für alle Filialen übernehmen."
                                testId="logo-branch-override" />
                        </div>
                        {form.company_logo_url && !isLocked("company_logo_url") && (
                            <button
                                type="button"
                                onClick={() => update("company_logo_url", "")}
                                className="mt-1 text-[11px] text-red-600 hover:underline w-full text-center"
                                data-testid="dealer-logo-remove-btn"
                            >
                                Entfernen
                            </button>
                        )}
                    </div>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Firmenname</Label>
                            <Input
                                value={form.company_name}
                                onChange={(e) => update("company_name", e.target.value)}
                                placeholder="Mustermann Motorräder GmbH"
                                data-testid="dealer-company-name-input"
                                {...lockProps("company_name", "rounded-sm mt-1 font-bold")}
                            />
                            <p className="text-[11px] text-gray-500 mt-1">Wird auf Inseraten, Rechnungen und im Audit-Export angezeigt.{isLocked("company_name") ? " Vom Hauptsitz zentral vorgegeben." : ""}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Kundennummer</Label>
                                <Input
                                    value={currentUser.customer_number || "—"}
                                    readOnly
                                    className="rounded-sm mt-1 font-mono-num bg-gray-100 text-gray-700"
                                    data-testid="dealer-customer-number"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">System-Posteingang</Label>
                                <Input
                                    value={currentUser.dealer_alias_email || "—"}
                                    readOnly
                                    className="rounded-sm mt-1 font-mono-num text-xs bg-gray-100 text-gray-700"
                                    data-testid="dealer-alias-email"
                                />
                                <p className="text-[11px] text-gray-500 mt-1">Nachrichten an diese Adresse werden automatisch an Ihre Hauptadresse weitergeleitet. Änderungen an Kundennummer &amp; System-Posteingang sind nur durch den Admin möglich.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Geschäftsführer / Inhaber (Jun-2026, User-Wunsch, Etappe 2) — manuell erfasst.
                    Zusätzlich: Ansprechpartner als Mitarbeiter wählbar ODER = GF/Inhaber. */}
                <div className="mt-4 pt-4 border-t border-[#FDE68A]/80" data-testid="dealer-user-block">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#92400E]">Geschäftsführer / Inhaber</div>
                        <div className="ml-auto">
                            <BranchOverrideToggle
                                area="ansprechpartner"
                                currentUser={currentUser}
                                infoText="Geschäftsführer / Inhaber & Ansprechpartner des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                                testId="ansprechpartner-branch-override"
                            />
                        </div>
                    </div>
                    {anyLocked && isLocked("contact_first_name") && (
                        <div className="flex items-start gap-2 rounded-sm border border-gray-200 bg-gray-50 p-2.5 mb-3" data-testid="gf-locked-by-hq-hint">
                            <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-gray-600">Vom <strong>Hauptsitz</strong> zentral vorgegeben — hier nur sichtbar, nicht bearbeitbar. Die Ansprechpartner-Auswahl bleibt frei.</p>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Vorname</Label>
                            <Input
                                value={form.contact_first_name}
                                onChange={(e) => update("contact_first_name", e.target.value)}
                                placeholder="Max"
                                {...lockProps("contact_first_name", "rounded-sm mt-1")}
                                data-testid="dealer-contact-first-name"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Name</Label>
                            <Input
                                value={form.contact_last_name}
                                onChange={(e) => update("contact_last_name", e.target.value)}
                                placeholder="Mustermann"
                                {...lockProps("contact_last_name", "rounded-sm mt-1")}
                                data-testid="dealer-contact-last-name"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">E-Mail-Adresse</Label>
                            <Input
                                type="email"
                                value={form.contact_email}
                                onChange={(e) => update("contact_email", e.target.value)}
                                placeholder="max@firma.de"
                                {...lockProps("contact_email", "rounded-sm mt-1")}
                                data-testid="dealer-contact-email"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Handynummer</Label>
                            <Input
                                value={form.phone_mobile}
                                onChange={(e) => update("phone_mobile", e.target.value)}
                                placeholder="+49 170 1234567"
                                {...lockProps("phone_mobile", "rounded-sm mt-1 font-mono-num")}
                                data-testid="dealer-phone-mobile"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Telefonnummer</Label>
                            <Input
                                value={form.phone_office}
                                onChange={(e) => update("phone_office", e.target.value)}
                                placeholder="+49 89 1234567"
                                {...lockProps("phone_office", "rounded-sm mt-1 font-mono-num")}
                                data-testid="dealer-phone-office"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Sammelrufnummer (Standort)</Label>
                            <Input
                                value={form.collective_phone}
                                onChange={(e) => update("collective_phone", e.target.value)}
                                placeholder="+49 89 1234-0"
                                className="rounded-sm mt-1 font-mono-num"
                                data-testid="dealer-collective-phone"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">Erscheint automatisch in der E-Mail-Signatur dieses Standortes.</p>
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Sammel-E-Mail (Standort)</Label>
                            <Input
                                type="email"
                                value={form.collective_email}
                                onChange={(e) => update("collective_email", e.target.value)}
                                placeholder="info@firma.de"
                                className="rounded-sm mt-1"
                                data-testid="dealer-collective-email"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">Erscheint automatisch in der E-Mail-Signatur dieses Standortes.</p>
                        </div>
                    </div>
                    {/* Ansprechpartner — Mitarbeiter wählen ODER = GF/Inhaber */}
                    <div className="mt-4 pt-4 border-t border-[#FDE68A]/60" data-testid="ansprechpartner-block">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#92400E] mb-2">Ansprechpartner</div>
                        {isLocked("ansprechpartner_employee_id") && (
                            <p className="text-[11px] text-gray-500 mb-2 border border-gray-200 bg-gray-50 rounded-sm px-2 py-1.5" data-testid="ansprechpartner-locked-hint">
                                Vom <strong>Hauptsitz</strong> zentral vorgegeben — Änderung nur über den Filial-Assistenten des Hauptsitzes.
                            </p>
                        )}
                        <div className={isLocked("ansprechpartner_employee_id") ? "opacity-50 pointer-events-none" : ""}>
                        <CheckRow
                            label="Ansprechpartner ist gleich Geschäftsführer / Inhaber"
                            checked={form.ansprechpartner_same_as_gf}
                            onChange={(v) => { update("ansprechpartner_same_as_gf", v); if (v) update("ansprechpartner_employee_id", ""); }}
                            testId="ansprechpartner-same-as-gf-checkbox"
                        />
                        {!form.ansprechpartner_same_as_gf && (
                            <div className="mt-1" data-testid="ansprechpartner-employee-row">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Ansprechpartner aus Mitarbeitern wählen</Label>
                                <div className="mt-1 max-w-md">
                                    <EmployeeSelect
                                        employees={ownEmployees}
                                        value={form.ansprechpartner_employee_id}
                                        onSelect={(id) => {
                                            // NUR die Ansprechpartner-Zuordnung speichern. Die
                                            // Geschäftsführer/Inhaber-Stammdaten (Name/E-Mail) dürfen
                                            // dadurch NICHT verändert werden — der Backend-Header/Signatur
                                            // löst Name & Foto eigenständig über die Mitarbeiter-ID auf.
                                            update("ansprechpartner_employee_id", id || "");
                                        }}
                                        placeholder="— Mitarbeiter wählen —"
                                        testId="ansprechpartner-employee-select"
                                    />
                                </div>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Anschrift */}
            <div id="dealer-standort-settings" className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 scroll-mt-24 transition-shadow" data-testid="dealer-standort-settings">
                <div className="sm:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Straße & Hausnr.</Label>
                    <Input value={form.street} onChange={(e) => update("street", e.target.value)} className="rounded-sm mt-1" data-testid="dealer-street" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">PLZ</Label>
                    <Input value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="dealer-postal" />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Ort</Label>
                    <Input value={form.city} onChange={(e) => update("city", e.target.value)} className="rounded-sm mt-1" data-testid="dealer-city" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Land</Label>
                    <Input value={form.country} onChange={(e) => update("country", e.target.value)} className="rounded-sm mt-1" data-testid="dealer-country" />
                </div>
                <div className="sm:col-span-3">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Standort-Code (4 Großbuchstaben)</Label>
                    <Input
                        value={form.site_code}
                        onChange={(e) => update("site_code", e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))}
                        placeholder="FBWE"
                        data-testid="dealer-site-code"
                        {...lockProps("site_code", "rounded-sm mt-1 font-mono-num uppercase max-w-[120px]")}
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                        Beispiel: <code className="font-mono-num">FBWE</code> für „Fürstenbrunner Weg". Wird automatisch als Prefix für jeden neu angelegten Lagerort verwendet — z.B. <code className="font-mono-num">FBWE-H1-R3-F02</code>.{isLocked("site_code") ? " Vom Hauptsitz zentral vorgegeben." : ""}
                    </p>
                </div>
            </div>
            <CheckRow
                label="Anschrift als Fahrzeugstandort übernehmen"
                hint="Bei neuen Inseraten wird automatisch diese Adresse als Standort verwendet."
                checked={form.use_address_as_listing_location}
                onChange={(v) => update("use_address_as_listing_location", v)}
                testId="use-address-checkbox"
            />

            {/* Phase 3.44 Iter 2 — Verkaufskategorien (von Werkstatt nach Stammdaten verschoben) */}
            <div className="mt-5 pt-5 border-t border-gray-100" data-testid="dealer-vehicle-categories-stamm">
                <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700 mb-1">Womit handeln Sie?</h4>
                <p className="text-xs text-gray-600 mb-3">Bestimmt das Marken-Routing, Inserate-Kategorien und Suchfilter.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                    <CheckRow
                        label="motorisierte Zweiräder"
                        hint="Verkauf von Motorrädern, Rollern, Mopeds, e-Schwalbe & Co."
                        checked={form.dealer_motorcycles}
                        onChange={(v) => update("dealer_motorcycles", v)}
                        testId="dealer-motorcycles-checkbox"
                    />
                    <CheckRow
                        label="Fahrräder"
                        hint="Verkauf von Fahrrädern, E-Bikes, Pedelecs"
                        checked={form.dealer_bicycles}
                        onChange={(v) => update("dealer_bicycles", v)}
                        testId="dealer-bicycles-checkbox"
                    />
                    {vehCats.auto && (
                        <CheckRow
                            label="Kraftfahrzeuge"
                            hint="Verkauf von Autos, Transportern & sonstigen Kraftfahrzeugen"
                            checked={form.dealer_cars}
                            onChange={(v) => update("dealer_cars", v)}
                            testId="dealer-cars-checkbox"
                        />
                    )}
                </div>
            </div>

            {/* Steuerdaten wurden in Phase 3.19 in die Unternehmensdaten verschoben. */}

            {/* Kontakt — Felder wurden in den Benutzer-Block unter Stammdaten verschoben. */}

            {/* Öffnungszeiten Verkauf (Phase 3.44 — von Werkstatt-Section in Stammdaten verschoben) */}
            <div className="mt-5 pt-5 border-t border-gray-100" data-testid="sales-hours-block">
                <CheckRow
                    label="Öffnungszeiten Verkauf"
                    hint="Werden auf der öffentlichen Händler-Seite und auf Ihren Inseraten angezeigt."
                    checked={form.sales_hours_enabled}
                    onChange={(v) => update("sales_hours_enabled", v)}
                    testId="sales-hours-checkbox"
                />
                {form.sales_hours_enabled && (
                    <div className="ml-7 mt-2" data-testid="sales-hours-input-row">
                        <Textarea
                            rows={4}
                            value={form.sales_hours}
                            onChange={(e) => update("sales_hours", e.target.value)}
                            placeholder={"Mo–Fr 09:00–18:00\nSa 09:00–13:00\nSo geschlossen"}
                            className="rounded-sm text-sm"
                            data-testid="sales-hours-input"
                        />
                    </div>
                )}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap rounded-sm border border-gray-200 bg-gray-50 px-4 py-3" data-testid="dealer-profile-autosave-row">
                <span className="text-xs text-gray-600">Änderungen werden automatisch gespeichert.</span>
                <div className="flex items-center gap-3">
                    <SaveStatusIndicator saving={sig.saving} dirty={sig.dirty} savedAt={sig.savedAt} />
                    <Button onClick={openImprint} disabled={imprintLoading} variant="outline" className="border-[#111827] text-[#111827] font-bold rounded-sm" data-testid="open-imprint-btn">
                        {imprintLoading ? "Lädt…" : "Impressum prüfen & bestätigen"}
                    </Button>
                </div>
            </div>

            {/* Phase 3.43 — Unternehmensdaten-Subsections jetzt am Ende der Stammdaten */}
            <UnternehmensdatenBlock currentUser={currentUser} />

            {/* Impressum-Modal — erscheint nach Profil-Speichern, vor finalem Confirm */}
            {imprintOpen && (
                <div
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                    onClick={() => !saving && dismissImprint()}
                    data-testid="imprint-modal-overlay"
                >
                    <div
                        className="bg-white border-2 border-[#FFCC00] rounded-sm w-full max-w-2xl max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="imprint-modal"
                    >
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <div>
                                <h3 className="font-display font-bold text-lg">Impressum bestätigen</h3>
                                <p className="text-xs text-gray-600">
                                    Automatisch aus Ihren Stammdaten generiert. Bitte prüfen und ggf. anpassen — der Text erscheint auf jedem Inserat als „Verantwortlich für dieses Inserat".
                                </p>
                            </div>
                            <button
                                onClick={() => !saving && dismissImprint()}
                                className="text-gray-400 hover:text-gray-700 p-1"
                                disabled={saving}
                                data-testid="imprint-modal-close"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="px-6 py-4 overflow-y-auto flex-1">
                            <Textarea
                                value={imprintText}
                                onChange={(e) => setImprintText(e.target.value)}
                                rows={16}
                                maxLength={4000}
                                disabled={imprintLoading || saving}
                                className="rounded-sm font-mono text-xs whitespace-pre-line"
                                data-testid="imprint-modal-textarea"
                            />
                            <div className="text-[10px] text-gray-400 mt-1 text-right">{(imprintText || "").length}/4000</div>
                            {isHauptsitz && (
                                <label className="mt-3 flex items-start gap-2 rounded-sm border border-[#FFCC00] bg-[#FFFBEB] p-3 cursor-pointer" data-testid="imprint-apply-branches-label">
                                    <input
                                        type="checkbox"
                                        checked={applyToBranches}
                                        onChange={(e) => setApplyToBranches(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 accent-[#111827]"
                                        data-testid="imprint-apply-branches-checkbox"
                                    />
                                    <span className="text-xs text-[#111827]">
                                        <span className="font-bold">Für alle Standorte / Filialen übernehmen &amp; überschreiben</span>
                                        <span className="block text-[11px] text-gray-600 mt-0.5">Das gespeicherte Impressum wird auf alle Filialen des Hauptsitzes übertragen und dort überschrieben.</span>
                                    </span>
                                </label>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row gap-2 sm:items-center sm:justify-between bg-gray-50">
                            <button
                                onClick={regenerateImprint}
                                disabled={imprintLoading || saving}
                                className="text-xs font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 disabled:opacity-50"
                                data-testid="imprint-modal-regenerate"
                            >
                                ↻ Automatisch neu generieren
                            </button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={dismissImprint}
                                    disabled={saving}
                                    className="rounded-sm"
                                    data-testid="imprint-modal-cancel"
                                >
                                    Abbrechen
                                </Button>
                                <Button
                                    onClick={confirmImprint}
                                    disabled={saving}
                                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider"
                                    data-testid="imprint-modal-confirm"
                                >
                                    {saving ? "Speichert…" : "Impressum bestätigen & speichern"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            </div>
            )}
        </div>
    );
}

